import React, { type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type TouchEvent as ReactTouchEvent, useMemo, useState } from 'react'
import { Popover } from 'antd'
import { useHorizontalDragScroll } from '@hooks/useHorizontalDragScroll'
import { usePulseHighlight } from '@hooks/usePulseHighlight'

import { IWidgetContractsChartUI } from './types'

import { Button, Container, Divider, Empty, Flex, Grid, Loader, Toogle } from '@ui/index'
import imgEmty from '@img/empty.svg'
import { IconContracts, IconSettings } from '@img/icons'

import css from '../../modskill/applicationsChart/ui.module.scss'

type TChartViewMode = 'classic' | 'percent'
type TContractPulseKey = 'total' | 'signing' | 'education' | 'completed' | 'cancel'
type TContractChartItem = {
    total: number
    signing: number
    education: number
    completed: number
    cancel: number
    statusCounts?: { contractStatusID: number; count: number }[]
}
type TDayContractChartItem = TContractChartItem & {
    year: number
    month: number
    day: number
}
type TContractStatRowItem = TContractChartItem & Partial<TDayContractChartItem>

const CHART_MIN_VALUE_HEIGHT = 15
const CHART_AGGREGATED_MAX_HEIGHT = 260
const CHART_AGGREGATED_MIN_VISIBLE_HEIGHT = 12
const CHART_DEFAULT_CONTENT_MIN_HEIGHT = 300
const CHART_REDUCED_CONTENT_MIN_HEIGHT = 140
const CHART_CONTENT_VERTICAL_OFFSET = 48

const totalStyle = {
    background: 'linear-gradient(180deg, rgba(58, 63, 76, .96) 0%, rgba(18, 22, 31, .96) 100%)',
    boxShadow: '0 8px 16px rgba(24, 29, 42, .14), inset 0 1px 0 rgba(255, 255, 255, .22), inset -9px 0 18px rgba(0, 0, 0, .18), inset 0 -14px 22px rgba(0, 0, 0, .18)'
}

/** UI виджета - Статистика договоров Chart */
export const WidgetContractsChartUI = ({
    txtCurrentYear,
    txtCurrentMonth,
    showStatisticsData,
    contractStatuses,
    totalsYears,
    totalsDays,
    typeStats,
    yearDetailStatsContracts,
    monthDetailStatsContracts,
    itemsYear,
    itemsMonth,
    itemsDays,
    getMonthName,
    contractStatusColors,
    title,
}: IWidgetContractsChartUI) => {
    const [chartViewMode, setChartViewMode] = useState<TChartViewMode>('classic')
    const dragScrollProps = useHorizontalDragScroll()
    const { getPulseClassName, triggerPulse } = usePulseHighlight()
    const statusStyles = {
        total: totalStyle,
        signing: { background: contractStatusColors.signing },
        education: { background: contractStatusColors.education },
        completed: { background: contractStatusColors.completed },
        cancel: { background: contractStatusColors.cancel },
    }

    const currentYearNumber = Number(yearDetailStatsContracts?.value || txtCurrentYear?.value || new Date().getFullYear())
    const currentMonthNumber = Number(monthDetailStatsContracts?.value || new Date().getMonth() + 1)

    const getPercentValue = (value?: number, total?: number) => {
        if (!total || Number(total) <= 0) return 0
        return Math.round((Number(value) / Number(total)) * 1000) / 10
    }

    const getConversionPercent = (education?: number, completed?: number, total?: number) => {
        if (!total || Number(total) <= 0) return 0
        return Math.round(((Number(education) + Number(completed)) / Number(total)) * 1000) / 10
    }

    const getAvg = (total?: number, count?: number) => {
        if (!count || Number(count) <= 0) return 0
        return (Math.round((Number(total) / Number(count)) * 10) / 10) || 0
    }

    const getScaledHeight = (value: number, minValue: number, unitHeight = CHART_MIN_VALUE_HEIGHT, minVisibleHeight = 0) => {
        if (value <= 0 || minValue <= 0) return 0
        return Number(Math.max(minVisibleHeight, (value / minValue) * unitHeight).toFixed(2))
    }

    const getStackedSegmentHeights = (values: number[], minValue: number, unitHeight: number, minVisibleHeight = 0) => {
        return values.map((value) => getScaledHeight(value, minValue, unitHeight, minVisibleHeight))
    }

    const getStackedTotalHeight = (totalValue: number, minValue: number, unitHeight: number, segmentValues: number[] = [], minVisibleHeight = 0) => {
        if (totalValue <= 0 || minValue <= 0) return 0

        const totalHeight = getScaledHeight(totalValue, minValue, unitHeight, minVisibleHeight)
        const stackedSegmentsHeight = getStackedSegmentHeights(segmentValues, minValue, unitHeight, minVisibleHeight)
            .reduce((sum, height) => sum + height, 0)

        return Math.max(totalHeight, Math.ceil(stackedSegmentsHeight))
    }

    const getDisplayTotal = (total: number) => chartViewMode === 'percent' ? (total > 0 ? 100 : 0) : total
    const getDisplayStatus = (value: number, total: number) => chartViewMode === 'percent' ? getPercentValue(value, total) : value

    const getChartScaleValues = (item: { total: number; signing: number; education: number; completed: number; cancel: number }) => [
        getDisplayTotal(item.total),
        getDisplayStatus(item.signing, item.total),
        getDisplayStatus(item.education, item.total),
        getDisplayStatus(item.completed, item.total),
        getDisplayStatus(item.cancel, item.total)
    ]

    const getMinPositiveValue = (values: number[]) => {
        const positiveValues = values.filter((value) => value > 0)
        return positiveValues.length > 0 ? Math.min(...positiveValues) : 1
    }

    const getMaxPositiveValue = (values: number[]) => {
        const positiveValues = values.filter((value) => value > 0)
        return positiveValues.length > 0 ? Math.max(...positiveValues) : 1
    }

    const getScaleUnitHeight = (minValue: number, maxValue: number, maxHeight?: number) => {
        if (!maxHeight || minValue <= 0 || maxValue <= 0) return CHART_MIN_VALUE_HEIGHT
        return Math.min(CHART_MIN_VALUE_HEIGHT, maxHeight / (maxValue / minValue))
    }

    const getAdaptiveChartContentMinHeight = (columnHeights: number[]) => {
        const maxColumnHeight = Math.max(0, ...columnHeights)

        if (maxColumnHeight <= 0) return CHART_DEFAULT_CONTENT_MIN_HEIGHT

        return Math.min(
            CHART_DEFAULT_CONTENT_MIN_HEIGHT,
            Math.max(CHART_REDUCED_CONTENT_MIN_HEIGHT, Math.ceil(maxColumnHeight + CHART_CONTENT_VERTICAL_OFFSET))
        )
    }

    const getPulseClass = (pulseKey: TContractPulseKey) => {
        const pulseClassKey = getPulseClassName(pulseKey)

        return pulseClassKey ? css[pulseClassKey] : ''
    }

    const getPulseTargetClassName = (pulseKey: TContractPulseKey, ...classNames: string[]) => {
        return [css['pulse_clickable'], getPulseClass(pulseKey), ...classNames].filter(Boolean).join(' ')
    }

    const handlePulseMouseDown = (pulseKey: TContractPulseKey, stopPropagation = false) => (event: ReactMouseEvent<HTMLElement>) => {
        if (stopPropagation) event.stopPropagation()

        triggerPulse(pulseKey)
    }

    const handlePulseTouchStart = (pulseKey: TContractPulseKey, stopPropagation = false) => (event: ReactTouchEvent<HTMLElement>) => {
        if (stopPropagation) event.stopPropagation()

        triggerPulse(pulseKey)
    }

    const handlePulseKeyDown = (pulseKey: TContractPulseKey, stopPropagation = false) => (event: ReactKeyboardEvent<HTMLElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return

        event.preventDefault()

        if (stopPropagation) event.stopPropagation()

        triggerPulse(pulseKey)
    }

    const getPulseInteractionProps = (pulseKey: TContractPulseKey, stopPropagation = false) => ({
        role: 'button' as const,
        tabIndex: 0,
        onMouseDown: handlePulseMouseDown(pulseKey, stopPropagation),
        onTouchStart: handlePulseTouchStart(pulseKey, stopPropagation),
        onKeyDown: handlePulseKeyDown(pulseKey, stopPropagation),
    })

    const getWeekdayShortByDate = (item: Pick<TDayContractChartItem, 'year' | 'month' | 'day'>) => {
        const jsDay = new Date(item.year, item.month - 1, item.day).getDay()

        switch (jsDay) {
            case 1: return 'ПН'
            case 2: return 'ВТ'
            case 3: return 'СР'
            case 4: return 'ЧТ'
            case 5: return 'ПТ'
            case 6: return 'СБ'
            default: return 'ВС'
        }
    }

    const isPreviousMonthTailItem = (item: Partial<TDayContractChartItem>) => {
        return typeStats.value === 'day'
            && typeof item.day === 'number'
            && typeof item.month === 'number'
            && typeof item.year === 'number'
            && (item.year !== currentYearNumber || item.month !== currentMonthNumber)
    }

    const getMonthShortName = (monthNumber: number) => getMonthName(monthNumber).slice(0, 3)

    const dayScaleValues = itemsDays.reduce<number[]>((values, item) => [...values, ...getChartScaleValues(item)], [])
    const monthScaleValues = itemsMonth.reduce<number[]>((values, item) => [...values, ...getChartScaleValues(item)], [])
    const yearScaleValues = itemsYear.reduce<number[]>((values, item) => [...values, ...getChartScaleValues(item)], [])

    const minDayValue = getMinPositiveValue(dayScaleValues)
    const minMonthValue = getMinPositiveValue(monthScaleValues)
    const minYearValue = getMinPositiveValue(yearScaleValues)
    const monthUnitHeight = getScaleUnitHeight(minMonthValue, getMaxPositiveValue(monthScaleValues), CHART_AGGREGATED_MAX_HEIGHT)
    const yearUnitHeight = getScaleUnitHeight(minYearValue, getMaxPositiveValue(yearScaleValues), CHART_AGGREGATED_MAX_HEIGHT)
    const dayChartContentMinHeight = getAdaptiveChartContentMinHeight(itemsDays.map((item) => {
        const totalValue = getDisplayTotal(item.total)
        const segmentValues = [item.signing, item.education, item.completed, item.cancel].map((value) => getDisplayStatus(value, item.total))

        return getStackedTotalHeight(totalValue, minDayValue, CHART_MIN_VALUE_HEIGHT, segmentValues)
    }))
    const monthChartContentMinHeight = getAdaptiveChartContentMinHeight(itemsMonth.map((item) => {
        const totalValue = getDisplayTotal(item.total)
        const segmentValues = [item.signing, item.education, item.completed, item.cancel].map((value) => getDisplayStatus(value, item.total))

        return getStackedTotalHeight(totalValue, minMonthValue, monthUnitHeight, segmentValues, CHART_AGGREGATED_MIN_VISIBLE_HEIGHT)
    }))
    const yearChartContentMinHeight = getAdaptiveChartContentMinHeight(itemsYear.map((item) => {
        const totalValue = getDisplayTotal(item.total)
        const segmentValues = [item.signing, item.education, item.completed, item.cancel].map((value) => getDisplayStatus(value, item.total))

        return getStackedTotalHeight(totalValue, minYearValue, yearUnitHeight, segmentValues, CHART_AGGREGATED_MIN_VISIBLE_HEIGHT)
    }))

    const monthYearGroups = itemsMonth.reduce<Array<{ year: number; count: number }>>((acc, item) => {
        const last = acc[acc.length - 1]

        if (last && last.year === item.year) {
            last.count += 1
        } else {
            acc.push({ year: item.year, count: 1 })
        }

        return acc
    }, [])

    const currentMonthDayItems = itemsDays.filter((item) => !isPreviousMonthTailItem(item))
    const currentMonthDayTotals = currentMonthDayItems.length === itemsDays.length
        ? totalsDays
        : currentMonthDayItems.reduce<CommonStatsContractsType>((acc, curr) => ({
            signing: acc.signing + curr.signing,
            education: acc.education + curr.education,
            completed: acc.completed + curr.completed,
            cancel: acc.cancel + curr.cancel,
            own: acc.own + curr.own,
            total: acc.total + curr.total,
        }), { signing: 0, education: 0, completed: 0, cancel: 0, own: 0, total: 0 })

    const currentGraphItems = typeStats.value === 'day'
        ? currentMonthDayItems
        : typeStats.value === 'month'
        ? itemsMonth
        : itemsYear

    const currentGraphStatusTotals = useMemo(() => {
        const totals = new Map<number, number>()

        currentGraphItems.forEach((item) => {
            item.statusCounts?.forEach((status) => {
                totals.set(status.contractStatusID, (totals.get(status.contractStatusID) || 0) + status.count)
            })
        })

        return totals
    }, [currentGraphItems])

    const currentGraphTotal = currentGraphItems.reduce((sum, item) => sum + item.total, 0)
    const currentGraphSigning = currentGraphItems.reduce((sum, item) => sum + item.signing, 0)
    const currentGraphEducation = currentGraphItems.reduce((sum, item) => sum + item.education, 0)
    const currentGraphCompleted = currentGraphItems.reduce((sum, item) => sum + item.completed, 0)
    const currentGraphCancel = currentGraphItems.reduce((sum, item) => sum + item.cancel, 0)

    const getStatusCount = (contractStatusID: number, statusCounts?: { contractStatusID: number; count: number }[]) => {
        return statusCounts?.find((item) => item.contractStatusID === contractStatusID)?.count ?? 0
    }

    const renderContractStatsHeaderRows = () => (
        <>
            {contractStatuses.map((status, index) => (
                <p
                    key={`contract-chart-header-${status.contractStatusID}`}
                    className={`${css['chart_stat_label']} ${index === 0 ? css['chart_stat_label_first'] : ''}`}
                    style={{ background: status.contractStatusColor }}
                >
                    {status.contractStatusName} ({currentGraphStatusTotals.get(status.contractStatusID) ?? 0}):
                </p>
            ))}

            <p className={css['chart_stat_label']} style={statusStyles.total}>Всего ({currentGraphTotal}):</p>
        </>
    )

    const renderContractStatsRows = (items: TContractStatRowItem[]) => (
        <div className={css['chart_stats_rows']}>
            {contractStatuses.map((status, rowIndex) => (
                <div
                    key={`contract-chart-row-${status.contractStatusID}`}
                    className={`${css['chart_stats_row']} ${rowIndex === 0 ? css['chart_stats_row_first'] : ''}`}
                >
                    {items.map((item, index) => (
                        <div
                            key={`contract-chart-cell-${status.contractStatusID}-${index}`}
                            className={`${css['item']} ${css['month_cell']} ${css['chart_stat_value']} ${isPreviousMonthTailItem(item) ? css['chart_stat_value_previous'] : ''}`}
                            style={{ background: status.contractStatusColor }}
                        >
                            {getStatusCount(status.contractStatusID, item.statusCounts)}
                        </div>
                    ))}
                </div>
            ))}

            <div className={css['chart_stats_row']}>
                {items.map((item, index) => (
                    <div
                        key={`contract-chart-total-${index}`}
                        className={`${css['item']} ${css['month_cell']} ${css['chart_stat_value']} ${isPreviousMonthTailItem(item) ? css['chart_stat_value_previous'] : ''}`}
                        style={statusStyles.total}
                    >
                        {item.total}
                    </div>
                ))}
            </div>
        </div>
    )

    const renderDaysRow = (items: TDayContractChartItem[]) => (
        <div className={css['chart_days_row']}>
            {items.map((item, index) => (
                <div key={`day-${index}`} className={`${css['year']} ${css['month_cell']} ${css['item']} ${css['day_label_wrap']} ${isPreviousMonthTailItem(item) ? css['day_label_previous'] : ''}`}>
                    <span>{item.day}</span>
                    <span className={css['day_week_label']}>{getWeekdayShortByDate(item)}</span>
                    {isPreviousMonthTailItem(item) && <span className={css['day_month_badge']}>{getMonthShortName(item.month)}</span>}
                </div>
            ))}
        </div>
    )

    const contentSettingsStatsContracts = (
        <Flex gap='10px' flexDirection='column' className={css['font_style_content']}>
            <Flex gap="6px" flexDirection='column'>
                <h5 style={{ fontWeight: '800' }}>Параметры статистики</h5>

                <Grid gap='8px' gridTemplate='columns' gridTemplateColumns='1fr 1fr 1fr'>
                    <Button padding='0 10px' size='sm' flexGrow={1} onClick={() => typeStats.setValue('day')} variant={`${typeStats.value === 'day' ? 'primary' : 'primaryDark'}`}>День</Button>
                    <Button padding='0 10px' size='sm' flexGrow={1} onClick={() => typeStats.setValue('month')} variant={`${typeStats.value === 'month' ? 'primary' : 'primaryDark'}`}>Месяц</Button>
                    <Button padding='0 10px' size='sm' flexGrow={1} onClick={() => typeStats.setValue('year')} variant={`${typeStats.value === 'year' ? 'primary' : 'primaryDark'}`}>Год</Button>
                </Grid>
            </Flex>

            <Divider variant='dark' />

            <Grid gridTemplate="columns" gridTemplateColumns="1fr auto" alignItems="center" className={css['panel_checkbox']}>
                <p>Статистические данные</p>
                <Toogle checked={showStatisticsData.value} onChange={() => showStatisticsData.setValue(!showStatisticsData.value)} />
            </Grid>

            <Divider variant='dark' />

            <Flex gap="6px" flexDirection='column'>
                <h5 style={{ fontWeight: '800' }}>Режим графика</h5>

                <Grid gap='8px' gridTemplate='columns' gridTemplateColumns='1fr 1fr'>
                    <Button padding='0 10px' size='sm' flexGrow={1} onClick={() => setChartViewMode('classic')} variant={`${chartViewMode === 'classic' ? 'primary' : 'primaryDark'}`}>Классический</Button>
                    <Button padding='0 10px' size='sm' flexGrow={1} onClick={() => setChartViewMode('percent')} variant={`${chartViewMode === 'percent' ? 'primary' : 'primaryDark'}`}>В процентах</Button>
                </Grid>
            </Flex>
        </Flex>
    )

    const contentYearsStatsContracts = (
        <Flex gap='4px' flexDirection='column'>
            {itemsYear.length === 0
                ? <Empty image={imgEmty} size='100px' />
                : itemsYear.map((item, index) => (
                    <Button key={index} padding='0 10px' size='sm' flexGrow={1}
                        onClick={() => yearDetailStatsContracts.setValue(`${item.year}`)}
                        variant={`${yearDetailStatsContracts.value === `${item.year}` ? 'primary' : 'primaryDark'}`}
                    >{item.year}</Button>
                ))
            }
        </Flex>
    )

    const contentMonthsStatsContracts = (
        <Flex gap='4px' flexDirection='column'>
            {itemsMonth.filter((item) => item.year === Number(yearDetailStatsContracts.value)).length === 0
                ? <Empty image={imgEmty} size='100px' />
                : itemsMonth.filter((item) => item.year === Number(yearDetailStatsContracts.value)).map((item, index) => (
                    <Button key={index} padding='0 10px' size='sm' flexGrow={1}
                        onClick={() => monthDetailStatsContracts.setValue(`${item.month}`)}
                        variant={`${monthDetailStatsContracts.value === `${item.month}` ? 'primary' : 'primaryDark'}`}
                    >{getMonthName(item.month)}</Button>
                ))
            }
        </Flex>
    )

    const getBlocksStats = () => {
        const displayedDayCount = currentMonthDayItems.length || new Date().getDate()

        switch (typeStats.value) {
            case 'day': return <>
                <Container className={css['block_stat_item']} background="rgba(0, 0, 0, .2)"><p style={{ fontWeight: '600', fontSize: '13px' }}>ВСЕГО</p><p style={{ fontWeight: '800' }}>{currentMonthDayTotals?.total ?? 0}</p></Container>
                <Container className={css['block_stat_item']} background="rgba(0, 160, 110, .7)"><p style={{ fontWeight: '600', fontSize: '13px' }}>СВОИ</p><p style={{ fontWeight: '800' }}>{currentMonthDayTotals?.own ?? 0}</p></Container>
                <Container className={css['block_stat_item']} background="rgba(255, 255, 0, .6)"><p style={{ fontWeight: '600', fontSize: '13px' }}>СРЕДНЕЕ/ДЕНЬ</p><p style={{ fontWeight: '800' }}>{getAvg(currentMonthDayTotals?.total, displayedDayCount)}</p></Container>
                <Container className={css['block_stat_item']} background="rgba(0, 0, 255, .2)"><p style={{ fontWeight: '600', fontSize: '13px' }}>КОНВЕРСИЯ</p><p style={{ fontWeight: '800' }}>{getConversionPercent(currentMonthDayTotals?.education, currentMonthDayTotals?.completed, currentMonthDayTotals?.total)}%</p></Container>
            </>

            case 'month': return <>
                <Container className={css['block_stat_item']} background="rgba(0, 0, 0, .2)"><p style={{ fontWeight: '600', fontSize: '13px' }}>ВСЕГО</p><p style={{ fontWeight: '800' }}>{totalsYears?.total ?? 0}</p></Container>
                <Container className={css['block_stat_item']} background="rgba(0, 160, 110, .7)"><p style={{ fontWeight: '600', fontSize: '13px' }}>СВОИ</p><p style={{ fontWeight: '800' }}>{totalsYears?.own ?? 0}</p></Container>
                <Container className={css['block_stat_item']} background="rgba(255, 255, 0, .6)"><p style={{ fontWeight: '600', fontSize: '13px' }}>СРЕДНЕЕ/МЕСЯЦ</p><p style={{ fontWeight: '800' }}>{getAvg(totalsYears?.total, itemsMonth.length)}</p></Container>
                <Container className={css['block_stat_item']} background="rgba(0, 0, 255, .2)"><p style={{ fontWeight: '600', fontSize: '13px' }}>КОНВЕРСИЯ</p><p style={{ fontWeight: '800' }}>{getConversionPercent(totalsYears?.education, totalsYears?.completed, totalsYears?.total)}%</p></Container>
            </>

            case 'year': return <>
                <Container className={css['block_stat_item']} background="rgba(0, 0, 0, .2)"><p style={{ fontWeight: '600', fontSize: '13px' }}>ВСЕГО</p><p style={{ fontWeight: '800' }}>{totalsYears?.total ?? 0}</p></Container>
                <Container className={css['block_stat_item']} background="rgba(0, 160, 110, .7)"><p style={{ fontWeight: '600', fontSize: '13px' }}>СВОИ</p><p style={{ fontWeight: '800' }}>{totalsYears?.own ?? 0}</p></Container>
                <Container className={css['block_stat_item']} background="rgba(255, 255, 0, .6)"><p style={{ fontWeight: '600', fontSize: '13px' }}>СРЕДНЕЕ/ГОД</p><p style={{ fontWeight: '800' }}>{getAvg(totalsYears?.total, itemsYear.length)}</p></Container>
                <Container className={css['block_stat_item']} background="rgba(0, 0, 255, .2)"><p style={{ fontWeight: '600', fontSize: '13px' }}>КОНВЕРСИЯ</p><p style={{ fontWeight: '800' }}>{getConversionPercent(totalsYears?.education, totalsYears?.completed, totalsYears?.total)}%</p></Container>
            </>
        }
    }

    const renderLegend = (label: string, value: number, style: React.CSSProperties, pulseKey: TContractPulseKey) => (
        <p className={getPulseTargetClassName(pulseKey, css['legend_item'])} style={style} {...getPulseInteractionProps(pulseKey)}>
            {chartViewMode === 'percent' ? `${label}, % (${value})` : `${label} (${value})`}
        </p>
    )

    const renderStackedInnerBars = (
        minValue: number,
        unitHeight: number,
        totalLabel: string | number,
        item: { total: number; signing: number; education: number; completed: number; cancel: number },
        minVisibleHeight = 0
    ) => {
        const segments = [
            { key: 'signing' as const, value: getDisplayStatus(item.signing, item.total), label: chartViewMode === 'percent' ? `${getPercentValue(item.signing, item.total)}%` : item.signing, style: statusStyles.signing },
            { key: 'education' as const, value: getDisplayStatus(item.education, item.total), label: chartViewMode === 'percent' ? `${getPercentValue(item.education, item.total)}%` : item.education, style: statusStyles.education },
            { key: 'completed' as const, value: getDisplayStatus(item.completed, item.total), label: chartViewMode === 'percent' ? `${getPercentValue(item.completed, item.total)}%` : item.completed, style: statusStyles.completed },
            { key: 'cancel' as const, value: getDisplayStatus(item.cancel, item.total), label: chartViewMode === 'percent' ? `${getPercentValue(item.cancel, item.total)}%` : item.cancel, style: statusStyles.cancel },
        ]
        const heights = getStackedSegmentHeights(segments.map((segment) => segment.value), minValue, unitHeight, minVisibleHeight)
        let bottom = 0

        return <>
            <p
                className={getPulseTargetClassName('total', css['item_header'], css['item_header_total'])}
                style={statusStyles.total}
                {...getPulseInteractionProps('total', true)}
            >
                {totalLabel}
            </p>
            {segments.map((segment, index) => {
                const height = heights[index]
                const segmentBottom = bottom
                bottom += height

                if (height <= 0) return null

                return (
                    <div
                        key={segment.key}
                        className={getPulseTargetClassName(segment.key, css['item'], css['item_inner_bar'])}
                        style={{ height: `${height}px`, bottom: `${segmentBottom}px`, ...segment.style }}
                        {...getPulseInteractionProps(segment.key, true)}
                    >
                        <p className={css['item_header_inner']}>{segment.label}</p>
                    </div>
                )
            })}
        </>
    }

    const renderChartColumn = (
        item: TContractChartItem,
        index: number,
        minValue: number,
        unitHeight: number,
        minVisibleHeight = 0,
        bottomContent?: React.ReactNode,
        columnClassName?: string
    ) => {
        const totalValue = getDisplayTotal(item.total)
        const segmentValues = [item.signing, item.education, item.completed, item.cancel].map((value) => getDisplayStatus(value, item.total))
        const totalHeight = getStackedTotalHeight(totalValue, minValue, unitHeight, segmentValues, minVisibleHeight)

        return (
            <div key={index} className={[css['chart_col'], columnClassName].filter(Boolean).join(' ')}>
                <div className={css['inset']}>
                    <div
                        hidden={item.total === 0}
                        className={`${css['item']} ${css['item_total']}`}
                        style={{ height: `${totalHeight}px`, ...statusStyles.total }}
                    >
                        {item.total > 0 && renderStackedInnerBars(
                            minValue,
                            unitHeight,
                            chartViewMode === 'percent' ? '100%' : item.total,
                            item,
                            minVisibleHeight
                        )}
                    </div>
                </div>

                {bottomContent}
            </div>
        )
    }

    return <>
        <Container className={css['container']}>
            <div className={css['container_header']}>
                <Grid gap="10px" gridTemplate="columns" gridTemplateColumns="1fr auto">
                    <Flex gap='6px' flexDirection='row' alignItems='center'>
                        <IconContracts width="26px" height="26px" />
                        <h4>{title}</h4>
                    </Flex>

                    <Flex gap='10px' alignItems="center" flexDirection='row'>
                        <Flex gap='10px' flexDirection='row' isRendered={typeStats.value === 'day'}>
                            <Popover content={contentMonthsStatsContracts} title="Выберите месяц" trigger="click" placement="bottomRight" styles={{ root: { width: '180px' } }}>
                                <div><Button size='sm' variant='icon' padding='0 10px'>{txtCurrentMonth.value}</Button></div>
                            </Popover>

                            <Popover content={contentYearsStatsContracts} title="Выберите год" trigger="click" placement="bottomRight" styles={{ root: { width: '150px' } }}>
                                <div><Button size='sm' variant='icon' padding='0 10px'>{txtCurrentYear.value}</Button></div>
                            </Popover>

                            <Divider type="vertical" size="20px" variant={'dark'} />
                        </Flex>

                        <Popover content={contentSettingsStatsContracts} trigger="click" placement="bottomRight" styles={{ root: { width: '320px' } }}>
                            <div><Button size='sm' variant='icon'><IconSettings width='20px' height='20px' /></Button></div>
                        </Popover>
                    </Flex>
                </Grid>

                <Divider variant='dark' />

                <Flex flexDirection="row" className={`${css['block_stat_conteiner']} ${css['contracts_block_stat_conteiner']}`}>
                    {getBlocksStats()}
                </Flex>
            </div>

            {typeStats.value === 'day' &&
                <div className={css['chart_container']}>
                    <div className={css['header']}>
                        {renderLegend('Договоры', currentGraphTotal, statusStyles.total, 'total')}
                        {renderLegend('На подпись', currentGraphSigning, statusStyles.signing, 'signing')}
                        {renderLegend('Обучаются', currentGraphEducation, statusStyles.education, 'education')}
                        {renderLegend('Закончили', currentGraphCompleted, statusStyles.completed, 'completed')}
                        {renderLegend('Отказ', currentGraphCancel, statusStyles.cancel, 'cancel')}
                        <div style={{ flexGrow: 1 }}></div>
                        <p className={css['year']}>День:</p>
                        {showStatisticsData.value && renderContractStatsHeaderRows()}
                    </div>

                    <div className={css['chart_scroll']} {...dragScrollProps}>
                        <div className={css['chart_inner']}>
                            <div className={`${css['chart_content']} ${css['chart_content_external_labels']}`} style={{ minHeight: `${dayChartContentMinHeight}px` }}>
                                {itemsDays.length === 0
                                    ? <div className={css['chart_loader']}><Loader isOpen={true} backdrop /></div>
                                    : itemsDays.map((item, index) => renderChartColumn(
                                        item,
                                        index,
                                        minDayValue,
                                        CHART_MIN_VALUE_HEIGHT,
                                        0,
                                        undefined,
                                        isPreviousMonthTailItem(item) ? css['chart_col_previous'] : undefined
                                    ))
                                }
                            </div>

                            {itemsDays.length > 0 && renderDaysRow(itemsDays)}

                            {showStatisticsData.value && itemsDays.length > 0 && renderContractStatsRows(itemsDays)}
                        </div>
                    </div>
                </div>
            }

            {typeStats.value === 'month' &&
                <div className={css['chart_container']}>
                    <div className={css['header']}>
                        {renderLegend('Договоры', currentGraphTotal, statusStyles.total, 'total')}
                        {renderLegend('На подпись', currentGraphSigning, statusStyles.signing, 'signing')}
                        {renderLegend('Обучаются', currentGraphEducation, statusStyles.education, 'education')}
                        {renderLegend('Закончили', currentGraphCompleted, statusStyles.completed, 'completed')}
                        {renderLegend('Отказ', currentGraphCancel, statusStyles.cancel, 'cancel')}
                        <div style={{ flexGrow: 1 }}></div>
                        <p className={css['year']}>Месяц:</p>
                        <p className={css['year']}>Год:</p>
                        {showStatisticsData.value && renderContractStatsHeaderRows()}
                    </div>

                    <div className={css['chart_scroll']} {...dragScrollProps}>
                        <div className={css['chart_inner']}>
                            <div className={`${css['chart_content']} ${css['chart_content_external_labels']}`} style={{ minHeight: `${monthChartContentMinHeight}px` }}>
                                {itemsMonth.length === 0
                                    ? <div className={css['chart_loader']}><Loader isOpen={true} backdrop /></div>
                                    : itemsMonth.map((item, index) => renderChartColumn(item, index, minMonthValue, monthUnitHeight, CHART_AGGREGATED_MIN_VISIBLE_HEIGHT))
                                }
                            </div>

                            <div className={css['chart_months_row']}>
                                {itemsMonth.map((item, index) => (
                                    <div key={`month-${index}`} className={`${css['year']} ${css['month_label']} ${css['month_cell']} ${css['item']}`}>{item.month}</div>
                                ))}
                            </div>

                            <div className={css['chart_years_row']}>
                                {monthYearGroups.map((group, index) => (
                                    <div key={`${group.year}-${index}`} className={css['chart_year_group']} style={{ width: `calc(${group.count} * 32px + ${(group.count - 1)} * 5px)` }}>{group.year}</div>
                                ))}
                            </div>
                            {showStatisticsData.value && itemsMonth.length > 0 && renderContractStatsRows(itemsMonth)}
                        </div>
                    </div>
                </div>
            }

            {typeStats.value === 'year' &&
                <div className={css['chart_container']}>
                    <div className={css['header']}>
                        {renderLegend('Договоры', currentGraphTotal, statusStyles.total, 'total')}
                        {renderLegend('На подпись', currentGraphSigning, statusStyles.signing, 'signing')}
                        {renderLegend('Обучаются', currentGraphEducation, statusStyles.education, 'education')}
                        {renderLegend('Закончили', currentGraphCompleted, statusStyles.completed, 'completed')}
                        {renderLegend('Отказ', currentGraphCancel, statusStyles.cancel, 'cancel')}
                        <div style={{ flexGrow: 1 }}></div>
                        <p className={css['year']}>Год:</p>
                        {showStatisticsData.value && renderContractStatsHeaderRows()}
                    </div>

                    <div className={css['chart_scroll']} {...dragScrollProps}>
                        <div className={css['chart_inner']}>
                            <div className={css['chart_content']} style={{ minHeight: `${yearChartContentMinHeight}px` }}>
                                {itemsYear.length === 0
                                    ? <div className={css['chart_loader']}><Loader isOpen={true} backdrop /></div>
                                    : itemsYear.map((item, index) => renderChartColumn(item, index, minYearValue, yearUnitHeight, CHART_AGGREGATED_MIN_VISIBLE_HEIGHT,
                                        <div className={`${css['year']} ${css['item']}`}>{item.year}</div>
                                    ))
                                }
                            </div>
                            {showStatisticsData.value && itemsYear.length > 0 && renderContractStatsRows(itemsYear)}
                        </div>
                    </div>
                </div>
            }
        </Container>
    </>
}
